import { defaultSchema } from "@atlaskit/adf-schema/schema-default";
import { EmailSerializer } from "@atlaskit/email-renderer";
import dompurify from "dompurify";
import { DATE_FORMATS } from "#utils/constants";
import { formatLocalDateTime } from "#utils/datetime";
import { CommentBody, WorkflowComment } from "../../types";

export const getInitials = (name: string): string => {
  const parts = name.trim().split(/\s+/);
  if (parts.length === 0) return "?";
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return `${parts[0][0]}${parts[parts.length - 1][0]}`.toUpperCase();
};

export const formatCommentDate = (dateString: string): string => {
  return formatLocalDateTime(dateString, `${DATE_FORMATS["MM/DD/YYYY"].long} h:mm a`);
};

const decodeHtmlEntities = (html: string): string => {
  const textarea = document.createElement("textarea");
  textarea.innerHTML = html;
  return textarea.value;
};

const convertAdfToHtml = (adfDocument: unknown): string => {
  try {
    if (!adfDocument || typeof adfDocument !== "object") {
      return "";
    }

    const serializer = EmailSerializer.fromSchema(defaultSchema as never);
    const node = defaultSchema.nodeFromJSON(adfDocument);
    const rawHtml = serializer.serializeFragment(node.content);
    const decodedHtml = decodeHtmlEntities(rawHtml);

    return dompurify.sanitize(decodedHtml);
  } catch {
    return "";
  }
};

export const renderCommentBodyHtml = (body: CommentBody): string => {
  if (body.kind === "html") {
    return dompurify.sanitize(body.html);
  }
  return convertAdfToHtml(body.document);
};

export const htmlToPlainText = (html: string): string => {
  const sanitized = dompurify.sanitize(html);
  if (typeof document === "undefined") {
    let text = sanitized;
    let prev: string;
    do {
      prev = text;
      text = prev.replace(/<[^>]*>/g, "");
    } while (text !== prev);
    return text.replace(/\s+/g, " ").trim();
  }
  const el = document.createElement("div");
  el.innerHTML = sanitized;
  return (el.textContent ?? "").replace(/\s+/g, " ").trim();
};

/** Extracts plain text from a comment body (strips all HTML tags). */
export const adfToPlainText = (body: CommentBody): string => {
  const html = renderCommentBodyHtml(body);
  if (typeof document === "undefined") return "";
  const el = document.createElement("div");
  el.innerHTML = html;
  return (el.textContent ?? "").replace(/\s+/g, " ").trim();
};

/**
 * Single-level threading: returns top-level comments (no parent) and their
 * direct replies. Replies referencing a missing or nested parent are promoted
 * to the top level so they are never hidden from the user.
 */
export type CommentThread = {
  parent: WorkflowComment;
  replies: WorkflowComment[];
};

export const buildThreads = (comments: WorkflowComment[]): CommentThread[] => {
  const byId = new Map<string, WorkflowComment>();
  comments.forEach((c) => byId.set(c.id, c));

  const repliesByParent = new Map<string, WorkflowComment[]>();
  const orphans: WorkflowComment[] = [];
  const tops: WorkflowComment[] = [];

  for (const comment of comments) {
    if (comment.parentCommentId === null) {
      tops.push(comment);
      continue;
    }
    const parent = byId.get(comment.parentCommentId);
    if (parent && parent.parentCommentId === null) {
      const list = repliesByParent.get(parent.id) ?? [];
      list.push(comment);
      repliesByParent.set(parent.id, list);
    } else {
      orphans.push(comment);
    }
  }

  const sortByCreatedAtAsc = (a: WorkflowComment, b: WorkflowComment): number =>
    new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime();

  const orderedTops = [...tops, ...orphans].sort(sortByCreatedAtAsc);

  return orderedTops.map((parent) => ({
    parent,
    replies: (repliesByParent.get(parent.id) ?? []).slice().sort(sortByCreatedAtAsc),
  }));
};
